import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  FlatList,
  Keyboard,
} from "react-native";
import { useTranslation } from "react-i18next";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../common/FloatingTextInputComponent";
import {
  validateField,
  getFieldMax,
  getFieldMin,
} from "../../../../helper/fieldHelper";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepAction } from "../hooks/useStepAction";
import type { FieldInfo } from "../models/fieldInfo";
import type { EnumerationItem } from "../models/EnumerationItem";

const dobRegex =
  /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;

function formatDobInput(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
  const mm = digits.slice(0, 2);
  const dd = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  if (digits.length <= 2) return mm;
  if (digits.length <= 4) return `${mm}/${dd}`;
  return `${mm}/${dd}/${yyyy}`;
}

function isDateField(field: FieldInfo) {
  return (field.dataType || "").toLowerCase() === "date";
}

function isEnumField(field: FieldInfo) {
  return (
    (field.dataType || "").toLowerCase() === "enum" &&
    Array.isArray(field.enumerationItem) &&
    field.enumerationItem.length > 0
  );
}

export function SenderDobScreen() {
  const { t } = useTranslation();
  const fieldInfo =
    useJourneyStore(
      (state) => state.journeyData.transactionFieldsSendResponse?.fieldInfo
    ) ?? [];
  const senderDob = useJourneyStore((state) => state.journeyData.senderDob);
  const senderPersonalDetails = useJourneyStore(
    (state) => state.journeyData.senderPersonalDetails
  );
  const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
  const { goNext } = useWorkflowNavigation();

  const personalDetailsFields = useMemo(() => {
    return fieldInfo
      .filter((f: FieldInfo) => f.field.startsWith("sender.personalDetails."))
      .sort(
        (a, b) =>
          Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
      );
  }, [fieldInfo]);

  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [enumDraft, setEnumDraft] = useState<Record<string, string>>({});
  const [focusedEnum, setFocusedEnum] = useState<string | null>(null);
  const enumBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      personalDetailsFields.forEach((f) => {
        if (next[f.field] !== undefined) return;
        if (isDateField(f)) {
          next[f.field] = senderDob;
        } else {
          next[f.field] = senderPersonalDetails[f.field] ?? "";
        }
      });
      return next;
    });
  }, [personalDetailsFields, senderDob, senderPersonalDetails]);

  const getLabel = (field: FieldInfo) => {
    const label = field.fieldLabel || field.field;
    if (field.required) return label;
    return `${label} (optional)`;
  };

  const clearError = (fieldKey: string) => {
    if (errors[fieldKey]) {
      setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
    }
  };

  const updateValue = (fieldKey: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldKey]: value }));
    clearError(fieldKey);
  };

  const filterEnumItems = (
    items: EnumerationItem[],
    query: string
  ) => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.description.toLowerCase().includes(q) ||
        i.value.toLowerCase().includes(q)
    );
  };

  const renderEnumField = (field: FieldInfo) => {
    const items = field.enumerationItem ?? [];
    const value = values[field.field] ?? "";
    const selected = items.find((i) => i.value === value);
    const draft = enumDraft[field.field];
    const inputValue = draft !== undefined ? draft : selected?.description ?? "";
    const filtered = filterEnumItems(items, inputValue);
    const showList = focusedEnum === field.field && filtered.length > 0;

    return (
      <View key={field.field}>
        <FloatingTextInputComponent
          testID={`sendMoney-${field.field}`}
          label={getLabel(field)}
          value={inputValue}
          onChangeText={(text) => {
            setEnumDraft((prev) => ({ ...prev, [field.field]: text }));
            const match = items.find(
              (i) =>
                i.description.toLowerCase() === text.trim().toLowerCase() ||
                i.value.toLowerCase() === text.trim().toLowerCase()
            );
            updateValue(field.field, match?.value ?? "");
          }}
          onFocus={() => {
            if (enumBlurTimerRef.current) {
              clearTimeout(enumBlurTimerRef.current);
            }
            setFocusedEnum(field.field);
          }}
          onBlur={() => {
            enumBlurTimerRef.current = setTimeout(() => {
              setFocusedEnum(null);
            }, 200);
          }}
          errorText={errors[field.field]}
        />
        {showList && (
          <View style={styles.dropdownContainer}>
            <FlatList
              keyboardShouldPersistTaps="handled"
              data={filtered}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.suggestionItem}
                  onPress={() => {
                    updateValue(field.field, item.value);
                    setEnumDraft((prev) => ({
                      ...prev,
                      [field.field]: item.description,
                    }));
                    setFocusedEnum(null);
                    Keyboard.dismiss();
                  }}
                >
                  <Text style={styles.suggestionText}>{item.description}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        )}
        {field.required && field.helpTextLong ? (
          <Text style={styles.helpText}>{field.helpTextLong}</Text>
        ) : null}
      </View>
    );
  };

  const renderDateField = (field: FieldInfo, index: number) => {
    const value = values[field.field] ?? "";
    const isStartTyping = value.trim().length > 0;
    return (
      <View key={field.field}>
        <FloatingTextInputComponent
          testID={
            field.field.includes("dateOfBirth") ? "senderDOB" : `sendMoney-${field.field}`
          }
          label={isStartTyping ? "Date" : "MM/DD/YYYY"}
          value={value}
          isAutoFocus={index === 0}
          onChangeText={(raw) => updateValue(field.field, formatDobInput(raw))}
          errorText={errors[field.field]}
          keyboardType="number-pad"
          dobIsComplete={dobRegex.test(value)}
        />
        {field.required && field.helpTextLong ? (
          <Text style={styles.helpText}>{field.helpTextLong}</Text>
        ) : null}
      </View>
    );
  };

  const renderTextField = (field: FieldInfo, index: number) => {
    const max = getFieldMax(fieldInfo, field.field);
    const min = getFieldMin(fieldInfo, field.field);
    return (
      <View key={field.field}>
        <FloatingTextInputComponent
          testID={`sendMoney-${field.field}`}
          label={getLabel(field)}
          value={values[field.field] ?? ""}
          isAutoFocus={index === 0}
          maxLength={max ? Number(max) : undefined}
          minLength={min ? Number(min) : undefined}
          onChangeText={(val) => updateValue(field.field, val)}
          errorText={errors[field.field]}
        />
        {field.required && field.helpTextLong ? (
          <Text style={styles.helpText}>{field.helpTextLong}</Text>
        ) : null}
      </View>
    );
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    personalDetailsFields.forEach((f) => {
      newErrors[f.field] = validateField(
        fieldInfo,
        f.field,
        values[f.field] ?? ""
      );
    });
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      return;
    }

    const dobField = personalDetailsFields.find(isDateField);
    updateJourneyData({
      senderPersonalDetails: values,
      senderDob: dobField ? values[dobField.field] ?? "" : senderDob,
    });
    goNext();
  };

  useStepAction(handleNext);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      testID="sender-dob-screen"
    >
      <Text style={styles.title}>{t("SENDER_DOB_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_DOB_SUBTITLE")}</Text>

      {personalDetailsFields.map((field, index) => {
        if (isEnumField(field)) return renderEnumField(field);
        if (isDateField(field)) return renderDateField(field, index);
        return renderTextField(field, index);
      })}

      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>

    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  helpText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    color: Colors.subTitleColor,
    lineHeight: 16,
    marginTop: 4,
    marginBottom: 8,
  },
  dropdownContainer: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: Colors.textInputBackground,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderColor: Colors.borderGrey,
  },
  suggestionText: {
    fontSize: 16,
    fontFamily: ComponentStyles.textFamily,
    color: Colors.titleTextColor,
  },
  infoBox: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 8,
    marginBottom: 24,
  },
  infoText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    color: Colors.darkGrey,
  },
});
